import type { FC } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';

import { PageSearchResult } from '@/features/pages/types/page.types';

import { Input } from '@/shared/components/ui/input';
import { Switch } from '@/shared/components/ui/switch';

interface SearchModalContentProps {
  searchQuery: string;
  searchResults: PageSearchResult[];
  includeFullText: boolean;
  onSearchChange: (query: string) => void;
  onIncludeFullTextChange: (includeFullText: boolean) => void;
  onClickPage: (pageId: string, pageCollectionId?: string) => void;
}

export const SearchModalContent: FC<SearchModalContentProps> = ({
  searchQuery,
  searchResults,
  includeFullText,
  onSearchChange,
  onIncludeFullTextChange,
  onClickPage,
}) => {
  return (
    <>
      <SearchBox
        searchQuery={searchQuery}
        setSearchQuery={onSearchChange}
        includeFullText={includeFullText}
        setIncludeFullText={onIncludeFullTextChange}
      />
      <div className="px-3 py-2 max-h-[300px] overflow-y-auto">
        <h4 className="font-semibold px-2 mb-2">Notes</h4>
        {!searchResults.length && (
          <div className="flex items-center p-2 rounded mb-4">
            <span className="mr-2">{searchQuery ? '😟' : '🔍'}</span>
            {searchQuery ? 'No notes found...' : 'Type to search..'}
          </div>
        )}
        {searchResults.map((doc, index) => (
          <div
            key={`${doc.id}-${String(index)}`}
            className="flex items-start p-2 cursor-pointer hover:bg-accent hover:text-foreground rounded"
            onClick={() => {
              onClickPage(doc.id, doc.collectionId);
            }}
          >
            <span className="mr-2 mt-0.5">📄</span>
            <span className="min-w-0">
              <span className="block">{doc.title}</span>
              {doc.snippet && (
                <span className="block text-sm text-muted-foreground line-clamp-2">
                  {doc.snippet}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

export const SearchBox = ({
  searchQuery,
  setSearchQuery,
  includeFullText,
  setIncludeFullText,
}: {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  includeFullText: boolean;
  setIncludeFullText: (value: boolean) => void;
}) => {
  return (
    <div className="w-full border-b px-4 pt-4 pb-3">
      <div className="flex items-center">
        <div className="mb-1 flex w-full items-center rounded-lg bg-muted px-3 py-1">
          <SearchIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
          <Input
            type="text"
            placeholder={
              includeFullText ? 'Search notes by title or text...' : 'Search notes by title...'
            }
            className="h-8 border-none bg-transparent pr-8 text-sm outline-none ring-0 placeholder:text-muted-foreground focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
          />
          {searchQuery && (
            <button
              type="button"
              title="Clear search"
              className="-ml-8 inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearchQuery('');
              }}
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 pl-1 text-[11px] font-normal text-muted-foreground">
        <Switch
          id="full-text-search-switch"
          size="sm"
          checked={includeFullText}
          onCheckedChange={setIncludeFullText}
          aria-label="Enable full text search"
        />
        <label htmlFor="full-text-search-switch" className="cursor-pointer leading-none">
          Full text search
        </label>
      </div>
    </div>
  );
};
